import {
  KnownKioskTransactionType,
  KnownProjectDtoProjectType,
  KnownProjectModuleSettingsDtoName,
  SettingsSchemaDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useQueryGetProject } from '../../../../core/queries/projects.queries';
import { QueryKeys } from '../../../../core/queries/query-keys.constants';
import { useQueryListCarriers } from '../../../carriers/carriers.queries';
import { useMutationUpdateProjectModule, useQueryGetProjectModuleOptional } from '../../../project-modules/queries';
import { SettingsMapper } from '../../mappings/mappings';
import { EntityForm } from '../form.types';
import { ProjectModuleForm } from './form';
import { ProjectModuleFormOutput } from './form-output';
import { ProjectModuleFormSchema } from './form-schema';
import { projectModuleFormUISchema } from './ui-schema';
export function useEditProjectModuleForm(
  projectID?: string,
  moduleID?: string,
  formData?: ProjectModuleForm,
  readOnly: boolean = false
): EntityForm | null {
  const queryClient = useQueryClient();
  const module = useQueryGetProjectModuleOptional(projectID, moduleID);

  const defaultValues = useMemo(() => {
    return ProjectModuleForm.GetInitialData({ ...module.data?.data, schemaID: module.data?.schema.id });
  }, [module.data]);

  const initialFormOutput = useMemo(() => {
    return new ProjectModuleFormOutput(defaultValues);
  }, [defaultValues]);

  const currentFormOutput = useMemo(() => {
    return new ProjectModuleFormOutput({
      ...initialFormOutput,
      ...formData
    });
  }, [formData, initialFormOutput]);

  const shouldWaitForCarriers = module.data?.schema.name === KnownProjectModuleSettingsDtoName.DryCleaning;
  // We only want to fetch Carriers if the selected module is Dry Cleaning
  const parties = useQueryListCarriers(projectID, {
    enabled: shouldWaitForCarriers,
    allowedTransactionTypes: [KnownKioskTransactionType.DeliverInboundDryCleaning]
  });
  const project = useQueryGetProject(projectID);

  const carrierOptions = useMemo(() => {
    if (!shouldWaitForCarriers) {
      return [];
    }
    return (parties.data?.items || []).map((carrier) => ({
      type: 'string' as const,
      title: carrier.name,
      enum: [carrier.id] as [string]
    }));
  }, [parties.data?.items, shouldWaitForCarriers]);

  const formSchema = useMemo(() => {
    return ProjectModuleFormSchema.FromSettings(
      currentFormOutput,
      [module.data?.schema].filter((it): it is SettingsSchemaDto => !!it),
      carrierOptions,
      project.data?.projectType as KnownProjectDtoProjectType | undefined,
      readOnly
    );
  }, [currentFormOutput, module.data?.schema, carrierOptions, project.data?.projectType, readOnly]);

  const mutation = useMutationUpdateProjectModule();

  const onSubmit = useCallback(
    async (formOutput: ProjectModuleFormOutput) => {
      if (!projectID || !moduleID) {
        return;
      }
      const result = await mutation.mutateAsync({
        moduleID,
        projectID,
        settings: ProjectModuleFormOutput.ToSettingsData(formOutput)
      });
      queryClient.setQueryData([QueryKeys.PROJECTS, projectID, QueryKeys.PROJECT_MODULES, moduleID], result);
      return {
        entityId: result.id,
        parentEntityId: projectID
      };
    },
    [moduleID, mutation, projectID, queryClient]
  );

  if (!formSchema) {
    return null;
  }

  return {
    isLoading: module.isLoading || project.isLoading || (shouldWaitForCarriers && parties.isLoading),
    title: 'Module Settings',
    defaultValues,
    initialFormOutput: initialFormOutput,
    formSchema,
    uiSchema: projectModuleFormUISchema,
    getOutput(formData) {
      return new ProjectModuleFormOutput(formData);
    },
    onChangeInterceptor: (_, formData) => formData, // No specific interceptor needed
    onSubmit,
    isLocked: SettingsMapper.ProjectTypeToProjectModuleLockedStatus(
      project.data?.projectType as KnownProjectDtoProjectType | undefined
    ),
    lockedMessage: SettingsMapper.ProjectTypeToProjectModuleLockedMessage(
      project.data?.projectType as KnownProjectDtoProjectType | undefined
    )
  };
}
